import { useState } from 'react';
import { FileText, LayoutGrid, Rows3, PartyPopper, Check, X } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { StatusBadge } from '@/components/ui/Badge';
import { SlaBadge } from '@/components/ui/SlaBadge';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/form/Textarea';
import { FormGroup } from '@/components/form/FormGroup';
import { useToast } from '@/components/ui/useToast';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { approvePr, cancelPr, listPrs, type Pr } from '@/api/pr';
import { apiError } from '@/lib/apiError';
import { formatDate, formatINRSymbol } from '@/lib/format';
import { PrFormModal } from './PrFormModal';

/**
 * Dedicated approval queue for the PR approver (HOD). PRs are raised by the
 * sales executive on the Purchase Requisition page and land here as `Pending`;
 * a different person reviews, edits if needed, and approves or rejects them —
 * enforcing separation of duties.
 */
export function PrApprovalPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const docViewer = useDocViewer();
  const [view, setView] = useState<'card' | 'table'>('table');
  const [editing, setEditing] = useState<Pr | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Pr | null>(null);
  const [reason, setReason] = useState('');

  const query = useQuery({
    queryKey: ['pr-pending'],
    queryFn: () => listPrs({ status: 'Pending', pageSize: 200 }),
    refetchInterval: 60_000,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['pr-pending'] });
    qc.invalidateQueries({ queryKey: ['pr'] });
  }

  const approveMutation = useMutation({
    mutationFn: approvePr,
    onSuccess: (pr) => {
      invalidate();
      toast.pushStep(`PR ${pr.prNo} approved`, {
        body: 'Next step — the purchase team can fast-track a PO or send it to RFQ.',
        href: '/purchase/po',
        nextLabel: 'Open Purchase Orders',
        tag: `pr-approved-${pr.id}`,
      });
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Approve failed'), 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelPr(id, reason),
    onSuccess: () => {
      invalidate();
      setRejectTarget(null);
      setReason('');
      toast.push('PR rejected', 'info');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Reject failed'), 'error'),
  });

  const data = query.data?.data ?? [];

  return (
    <div>
      <SectionHeader
        title="PR Approval"      />

      {/* View toggle — segmented pill matching the app-wide tab style */}
      <div className="mb-2 flex items-center gap-1.5 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-1.5 backdrop-blur-xl">
        <div className="inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-1">
          <button
            type="button"
            onClick={() => setView('card')}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
              (view === 'card'
                ? 'bg-accent text-white shadow-sm'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
            }
            title="Card layout — full PR detail per card"
          >
            <LayoutGrid size={13} /> Cards
          </button>
          <button
            type="button"
            onClick={() => setView('table')}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
              (view === 'table'
                ? 'bg-accent text-white shadow-sm'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
            }
            title="Table layout — one row per PR"
          >
            <Rows3 size={13} /> Table
          </button>
        </div>
      </div>

      {query.isFetching && data.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center text-sm text-[var(--text-muted)]">
          Loading&hellip;
        </div>
      ) : data.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center text-sm text-[var(--text-muted)]">
          <PartyPopper size={16} className="mr-1 inline-block align-[-3px]" /> No pending approvals
        </div>
      ) : view === 'card' ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.map((r) => (
            <div
              key={r.id}
              className="flex flex-col gap-2 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-base font-extrabold text-accent">{r.prNo}</div>
                  <div className="text-[11px] text-[var(--text-muted)]">
                    {formatDate(r.prDate)} · {r.department}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge status={r.status} />
                  <SlaBadge sla={r.sla ?? null} />
                </div>
              </div>

              {r.subject && <div className="truncate text-xs font-semibold text-[var(--text-secondary)]" title={r.subject}>{r.subject}</div>}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <Meta label="MRN Ref">{r.mrn?.mrnNo ?? '—'}</Meta>
                <Meta label="Authority">{r.authority}</Meta>
                <Meta label="Items">{r.items.length}</Meta>
                <Meta label="Est. Value">{formatINRSymbol(r.totalValue)}</Meta>
              </div>

              <div className="mt-1 flex items-center gap-2 border-t border-[var(--border-color)] pt-2">
                <Button variant="glass" size="sm" onClick={() => setEditing(r)}>View</Button>
                <Button variant="glass" size="sm" onClick={() => docViewer.open('pr', r.id, r.prNo)} title="Open A4 document"><FileText size={14} /></Button>
                <Button
                  variant="success"
                  size="sm"
                  className="flex-1"
                  onClick={() => approveMutation.mutate(r.id)}
                  disabled={approveMutation.isPending}
                >
                  <Check size={14} className="mr-1 inline-block align-[-2px]" /> Approve
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="flex-1"
                  onClick={() => { setRejectTarget(r); setReason(''); }}
                >
                  <X size={14} className="mr-1 inline-block align-[-2px]" /> Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Table<Pr>
          storageKey="pr-approval"
          title="Pending Approvals"
          count={`Showing ${data.length}`}
          rows={data}
          rowKey={(r) => r.id}
          loading={query.isFetching && data.length === 0}
          empty={<><PartyPopper size={16} className="mr-1 inline-block align-[-3px]" /> No pending approvals</>}
          columns={[
            { key: 'prNo', header: 'PR #', width: '130px', cell: (r) => <span className="font-extrabold text-accent">{r.prNo}</span> },
            { key: 'date', header: 'Date', width: '110px', cell: (r) => formatDate(r.prDate) },
            { key: 'subject', header: 'Subject', width: '200px', cell: (r) => <span className="block max-w-[200px] truncate" title={r.subject ?? undefined}>{r.subject || '—'}</span> },
            { key: 'mrn', header: 'MRN Ref', width: '120px', cell: (r) => r.mrn?.mrnNo ? <span className="text-[11px] font-bold text-[#a78bfa]">{r.mrn.mrnNo}</span> : '—' },
            { key: 'dept', header: 'Dept', cell: (r) => r.department },
            { key: 'items', header: 'Items', width: '70px', align: 'right', cell: (r) => r.items.length },
            { key: 'value', header: 'Est. Value', width: '110px', align: 'right', cell: (r) => formatINRSymbol(r.totalValue) },
            { key: 'authority', header: 'Authority', width: '100px', cell: (r) => r.authority },
            { key: 'status', header: 'Status', width: '100px', cell: (r) => <StatusBadge status={r.status} /> },
            { key: 'sla', header: 'SLA', width: '60px', cell: (r) => <SlaBadge sla={r.sla ?? null} /> },
            {
              key: 'actions',
              header: '',
              width: '300px',
              cell: (r) => (
                <div className="flex flex-nowrap items-center justify-center gap-1">
                  <Button variant="glass" size="sm" onClick={() => setEditing(r)}>View</Button>
                  <Button variant="glass" size="sm" onClick={() => docViewer.open('pr', r.id, r.prNo)} title="Open A4 document"><FileText size={14} /></Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => approveMutation.mutate(r.id)}
                    disabled={approveMutation.isPending}
                  >
                    <Check size={14} className="mr-1 inline-block align-[-2px]" /> Approve
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => { setRejectTarget(r); setReason(''); }}
                  >
                    <X size={14} className="mr-1 inline-block align-[-2px]" /> Reject
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}

      <PrFormModal open={!!editing} editing={editing} onClose={() => setEditing(null)} />

      <Modal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title={`Reject ${rejectTarget?.prNo ?? ''}`}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={() => rejectTarget && rejectMutation.mutate({ id: rejectTarget.id, reason })}
              disabled={!reason.trim() || rejectMutation.isPending}
            >
              Confirm Reject
            </Button>
          </>
        }
      >
        <FormGroup label="Rejection Reason" required>
          <Textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this PR is being rejected…"
          />
        </FormGroup>
      </Modal>
    </div>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </div>
      <div className="truncate font-semibold text-[var(--text-primary)]">{children}</div>
    </div>
  );
}
